import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  TextField,
  Paper,
  Autocomplete,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";

import { useAppDispatch, useAppSelector } from "../../redux/store";
import ConfirmDialog from "../common/ConfirmDialog";
import AppButton from "../buttons/AppButton";

import {
  addPoItem,
  createPurchaseOrder,
} from "../../redux/actions/purchaseOrderActions";
import { purchaseOrderActions } from "../../redux/slices/purchaseOrder";
import { formatCurrency, formatPlainNumber } from "../../utils/appUtils";
import { InventoryItemResponseDto } from "../../shared/dtos/inventoryItem/InventoryItemResponseDto";
import { PurchaseOrderRequestDto } from "../../shared/dtos/purchaseOrder/PurchaseOrderRequestDto";
import { SupplierSummaryDto } from "../../shared/dtos/supplier/SupplierSummaryDto";

interface SupplierOption {
  id: string;
  label: string;
}

interface PoMobileViewProps {
  items: InventoryItemResponseDto[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

const PoMobileView: React.FC<PoMobileViewProps> = ({
  items,
  searchTerm,
  setSearchTerm,
}) => {
  const dispatch = useAppDispatch();
  const {
    cartItems,
    cartSubtotal,
    selectedSupplier,
    loadingCreate,
    serverStatus,
  } = useAppSelector((state) => state.purchaseOrder);
  const { items: suppliers } = useAppSelector((state) => state.supplier);
  const supplier = selectedSupplier as unknown as SupplierSummaryDto | null;
  const [isClearDialogOpen, setIsClearDialogOpen] = useState<boolean>(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] =
    useState<boolean>(false);

  const supplierOptions = useMemo(
    () => suppliers.map((s) => ({ id: s.id, label: s.name })),
    [suppliers],
  );

  const handleUpdateField = (
    inventoryItemId: string,
    field: "quantity" | "unitCost",
    value: string | number,
  ): void => {
    if (value === "" || Number(value) >= 0) {
      dispatch(
        purchaseOrderActions.poItemUpdateFields({
          inventoryItemId,
          updates: { [field]: value },
        }),
      );
    }
  };

  const handleCreatePO = (): void => {
    if (!selectedSupplier) return;
    const payload: PurchaseOrderRequestDto = {
      supplierId: selectedSupplier.id,
      orderDate: new Date().toISOString().split("T")[0],
      items: cartItems.map((i) => ({
        inventoryItemId: i.inventoryItemId,
        quantity: Number(i.quantity),
        unitCost: Number(i.unitCost),
      })),
    };
    dispatch(createPurchaseOrder(payload));
  };

  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    return items.filter(
      (i) =>
        i.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.itemCode?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [items, searchTerm]);

  useEffect(() => {
    if (serverStatus?.operation === "create" && serverStatus?.status === 201) {
      setIsConfirmDialogOpen(false);
      setIsClearDialogOpen(false);
    }
  }, [serverStatus]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 64px)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: 1.5,
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Autocomplete
          options={supplierOptions}
          getOptionLabel={(opt: SupplierOption) => opt.label || ""}
          value={
            supplierOptions.find((o) => o.id === selectedSupplier?.id) || null
          }
          onChange={(_, val: SupplierOption | null) => {
            const fullSupplier =
              suppliers.find((s) => s.id === val?.id) ?? null;
            dispatch(purchaseOrderActions.setPoSupplier(fullSupplier));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Supplier"
              variant="outlined"
              size="small"
            />
          )}
          sx={{ mb: 2 }}
        />
      </Box>

      {/* CART SUMMARY */}
      <Box
        sx={{
          p: 1,
          bgcolor: "background.paper",
          flexShrink: 0,
          maxHeight: "30vh",
          overflowY: "auto",
        }}
      >
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", mb: 1, px: 1 }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <LocalShippingOutlinedIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              Items ({cartItems.length})
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            sx={{ fontWeight: "bold", color: "info.main" }}
          >
            Total: {formatPlainNumber(cartSubtotal)}
          </Typography>
        </Stack>
        <Stack spacing={1}>
          {cartItems.map((cItem) => (
            <Paper
              key={cItem.inventoryItemId}
              elevation={0}
              sx={{ p: 1.5, bgcolor: "background.default", borderRadius: 2 }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", mb: 1, display: "block" }}
                noWrap
              >
                {cItem.name}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: 1,
                  gridTemplateColumns: "1fr 1fr",
                  alignItems: "center",
                }}
              >
                <TextField
                  label="Unit Cost"
                  size="small"
                  type="number"
                  value={cItem.unitCost}
                  onChange={(e) =>
                    handleUpdateField(
                      cItem.inventoryItemId,
                      "unitCost",
                      e.target.value,
                    )
                  }
                />
                <Stack
                  direction="row"
                  sx={{ alignItems: "center", justifyContent: "flex-end" }}
                >
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleUpdateField(
                        cItem.inventoryItemId,
                        "quantity",
                        cItem.quantity - 1,
                      )
                    }
                  >
                    <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <TextField
                    size="small"
                    type="number"
                    value={cItem.quantity}
                    onChange={(e) =>
                      handleUpdateField(
                        cItem.inventoryItemId,
                        "quantity",
                        e.target.value,
                      )
                    }
                    sx={{ width: 60 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleUpdateField(
                        cItem.inventoryItemId,
                        "quantity",
                        cItem.quantity + 1,
                      )
                    }
                  >
                    <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() =>
                      dispatch(
                        purchaseOrderActions.poItemRemove(
                          cItem.inventoryItemId,
                        ),
                      )
                    }
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Stack>
              </Box>
            </Paper>
          ))}
        </Stack>
      </Box>

      {/* SEARCH */}
      <Box
        sx={{
          px: 2,
          py: 1,
          flexShrink: 0,
          boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
          zIndex: 10,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Scan SKU or Product Name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ bgcolor: "background.paper", borderRadius: 2 }}
        />
      </Box>

      {/* PRODUCTS */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 1.5, pb: 12 }}>
        <Stack spacing={1.5}>
          {filteredItems.map((item) => (
            <Paper
              key={item.id}
              elevation={0}
              onClick={() =>
                dispatch(
                  addPoItem({
                    id: item.id,
                    name: item.name,
                    unitCost: item.unitCost,
                    category: item.category || "N/A",
                  }),
                )
              }
              sx={{
                p: 1.5,
                bgcolor: "background.paper",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                "&:active": {
                  transform: "scale(0.98)",
                  bgcolor: "action.hover",
                },
              }}
            >
              <Stack
                direction="row"
                sx={{ justifyContent: "space-between", alignItems: "center" }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", mb: 0.5 }}
                  >
                    {item.name}
                  </Typography>
                </Box>
                <Stack sx={{ alignItems: "center" }}>
                  <Typography variant="caption" color="text.secondary">
                    Unit Cost
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "success.main", fontWeight: "bold" }}
                  >
                    {formatPlainNumber(item.unitCost)}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Box>

      {/* FOOTER */}
      <Paper
        elevation={24}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          display: "flex",
          gap: 2,
          zIndex: 100,
          borderRadius: "20px 20px 0 0",
        }}
      >
        <AppButton
          pill
          variant="outlined"
          color="error"
          fullWidth
          onClick={() => setIsClearDialogOpen(true)}
        >
          Clear
        </AppButton>
        <AppButton
          variant="contained"
          color="primary"
          fullWidth
          pill
          disabled={cartItems.length === 0 || !supplier}
          onClick={() => setIsConfirmDialogOpen(true)}
        >
          Submit PO
        </AppButton>
      </Paper>

      {isConfirmDialogOpen && supplier && (
        <ConfirmDialog
          open={isConfirmDialogOpen}
          onClose={() => setIsConfirmDialogOpen(false)}
          onConfirm={handleCreatePO}
          title="Confirm Order"
          description={`Confirm order of ${formatCurrency(cartSubtotal)} to ${supplier.name}?`}
          confirmText="Submit Order"
          loading={loadingCreate}
          severity="warning"
        />
      )}

      {isClearDialogOpen && (
        <ConfirmDialog
          open={isClearDialogOpen}
          onClose={() => setIsClearDialogOpen(false)}
          onConfirm={() => {
            dispatch(purchaseOrderActions.clearPoCart());
            setIsClearDialogOpen(false);
          }}
          title="Reset Order"
          description="Discard current draft?"
          confirmText="Reset"
        />
      )}
    </Box>
  );
};

export default PoMobileView;
